import { type ReactElement, type UIEvent, useState } from 'react';

interface TableData {
  wagered: number;
  returned: number;
  wins: number;
  total: number;
}

interface TableSectionProps {
  title: string;
  data: Record<string, TableData | null | undefined>;
  keyOrder?: string[];
}

export default function TableSection({ title, data, keyOrder }: TableSectionProps): ReactElement {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const keys = keyOrder || Object.keys(data);
  const hasScroll = keys.filter((k) => data[k]).length > 5;

  const handleScroll = (e: UIEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 10);
  };

  return (
    <div className="bg-theme-surface rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3 text-theme-text">{title}</h3>
      <div className="relative">
        <div
          className={`max-h-48 overflow-y-auto ${hasScroll ? 'pb-8' : ''}`}
          onScroll={handleScroll}
        >
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-theme-surface">
              <tr className="text-theme-text-secondary text-left">
                <th className="pb-2">Category</th>
                <th className="pb-2">Bets</th>
                <th className="pb-2">W-L</th>
                <th className="pb-2">Win%</th>
                <th className="pb-2">P/L</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => {
                const d = data[key];
                if (!d) return null;
                const pl = d.returned - d.wagered;
                return (
                  <tr key={key} className="border-t border-theme-border">
                    <td className="py-2 text-theme-text">{key}</td>
                    <td className="py-2 text-theme-text-secondary">{d.total}</td>
                    <td className="py-2 text-theme-text-secondary">
                      {d.wins}-{d.total - d.wins}
                    </td>
                    <td className="py-2 text-theme-text-secondary">
                      {((d.wins / d.total) * 100).toFixed(0)}%
                    </td>
                    <td className={`py-2 ${pl >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}>
                      {pl >= 0 ? '+' : ''}
                      {pl.toFixed(0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasScroll && !isAtBottom && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-theme-surface to-transparent pointer-events-none flex items-end justify-center pb-1">
            <span className="text-theme-text-tertiary text-xs">Scroll for more</span>
          </div>
        )}
      </div>
    </div>
  );
}
